import { useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { decisionState, FINAL_DECISION_LABEL } from "@/components/DecisionBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Gavel, Award, XCircle, MinusCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AdverseActionDialog from "@/components/AdverseActionDialog";
import { fmtDate } from "@/lib/dateFormat";

// Visual treatment per state. Final-decision states take precedence and use
// outcome-driven palettes (emerald / rose / slate). Otherwise we fall back to
// the NTN Recommendation palette for the "intelligence" layer.
const HERO_STYLE = {
  final_approved:     "bg-gradient-to-br from-emerald-700 to-emerald-600 text-white border-emerald-800 shadow-lg shadow-emerald-900/20",
  final_denied:       "bg-gradient-to-br from-rose-700 to-rose-600 text-white border-rose-800 shadow-lg shadow-rose-900/20",
  final_not_selected: "bg-gradient-to-br from-slate-700 to-slate-600 text-white border-slate-800 shadow-lg shadow-slate-900/20",
  approve:            "bg-gradient-to-br from-emerald-800 to-emerald-700 text-white border-emerald-900 shadow-lg shadow-emerald-900/20",
  conditional:        "bg-gradient-to-br from-amber-700 to-amber-600 text-white border-amber-800 shadow-lg shadow-amber-900/20",
  deny:               "bg-gradient-to-br from-rose-800 to-rose-700 text-white border-rose-900 shadow-lg shadow-rose-900/20",
  ready:              "bg-gradient-to-br from-slate-50 to-white border-slate-200 text-slate-900",
  awaiting:           "bg-gradient-to-br from-indigo-50 to-white border-indigo-200 text-slate-900",
  in_progress:        "bg-gradient-to-br from-[#F3EFEA] to-white border-slate-200 text-slate-900",
  submitted:          "bg-gradient-to-br from-[#F3EFEA] to-white border-slate-200 text-slate-900",
};

const HEADLINE = {
  final_approved:     "Approved",
  final_denied:       "Not Approved",
  final_not_selected: "Not Selected",
  approve:            "Recommended",
  conditional:        "Recommended with Conditions",
  deny:               "Not Recommended",
  ready:              "Decision-Ready Application",
  in_progress:        "Reports in progress",
  awaiting:           "Awaiting applicant",
  submitted:          "Just submitted",
};

const SUBLINE_NEUTRAL = {
  final_approved:     "This applicant has been selected and will move forward. Application closed and archived to the Closed Applications log.",
  final_denied:       "The Property Manager has declined this application. Closed and archived. An Adverse Action Notice may be required.",
  final_not_selected: "This applicant was qualified but not chosen due to availability or another applicant being selected. This is a neutral outcome and does NOT imply risk or denial. Closed and archived.",
  approve:            "NTN recommends this application proceed. The final leasing decision rests with the property manager.",
  conditional:        "NTN recommends proceeding subject to the conditions noted below. The final leasing decision rests with the property manager.",
  deny:               "NTN does not recommend this application. The final leasing decision rests with the property manager.",
  in_progress:        "We're running the requested reports — you'll be notified as each completes.",
  awaiting:           "The applicant has been emailed a secure link to complete their application.",
  submitted:          "Reports will start once the applicant completes their California-compliant application.",
};

const FINAL_STATES = ["final_approved", "final_denied", "final_not_selected"];
const RECOMMENDATION_STATES = ["approve", "conditional", "deny"];

const FINAL_OPTIONS = [
  { value: "approved",     label: "Approved",     blurb: "Selected applicant — will move forward.", Icon: Award,        tone: "emerald" },
  { value: "denied",       label: "Not Approved", blurb: "Declined. A reason is required (FCRA).",  Icon: XCircle,      tone: "rose" },
  { value: "not_selected", label: "Not Selected", blurb: "Qualified but not chosen. Neutral.",      Icon: MinusCircle,  tone: "slate" },
];

const OPTION_TONE = {
  emerald: { active: "border-emerald-600 ring-2 ring-emerald-500 bg-emerald-50", icon: "text-emerald-700 bg-emerald-100" },
  rose:    { active: "border-rose-600 ring-2 ring-rose-500 bg-rose-50",          icon: "text-rose-700 bg-rose-100" },
  slate:   { active: "border-slate-600 ring-2 ring-slate-500 bg-slate-100",      icon: "text-slate-700 bg-slate-200" },
};

export default function DecisionHero({ s, isStaff, isPM, reload }) {
  const state = decisionState(s);
  const isFinal = FINAL_STATES.includes(state);
  const isRecommendation = RECOMMENDATION_STATES.includes(state);
  const isDecided = isFinal || isRecommendation;

  // Staff-only "Set NTN Recommendation" dialog.
  const [recOpen, setRecOpen] = useState(false);
  const [rec, setRec] = useState(s.recommendation || "approve");
  const [reason, setReason] = useState(s.recommendation_reason || "");
  const [recBusy, setRecBusy] = useState(false);

  const submitRec = async () => {
    setRecBusy(true);
    try {
      await apiClient.patch(`/screenings/${s.id}/recommendation`, { recommendation: rec, reason });
      toast.success(`Recommendation set: ${rec.toUpperCase()}`);
      setRecOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRecBusy(false); }
  };

  // PM-only "Set Final Decision" dialog.
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionChoice, setDecisionChoice] = useState(s.final_decision || "approved");
  const [decisionReason, setDecisionReason] = useState(s.final_decision_reason || "");
  const [decisionBusy, setDecisionBusy] = useState(false);

  // PM-only "Undo Final Decision" dialog (P1 backlog — 24h correction window).
  // NTN Staff can undo any time.
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoReason, setUndoReason] = useState("");
  const [undoBusy, setUndoBusy] = useState(false);

  const submitUndo = async () => {
    if (undoReason.trim().length < 3) {
      toast.error("Please enter a reason (≥3 characters).");
      return;
    }
    setUndoBusy(true);
    try {
      await apiClient.post(`/screenings/${s.id}/final-decision/undo`, {
        reason: undoReason.trim(),
      });
      toast.success("Final Decision reversed. Application re-opened.");
      setUndoOpen(false);
      setUndoReason("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not undo decision.");
    } finally { setUndoBusy(false); }
  };

  // Correction window (matches backend UNDO_WINDOW_HOURS=24) — PMs only.
  // Staff bypass this constraint.
  const canUndoNow = (() => {
    if (!isFinal) return false;
    if (isStaff) return true;
    if (!isPM) return false;
    try {
      const stamped = new Date(s.final_decision_at).getTime();
      if (!stamped) return true; // legacy record with unparseable timestamp
      const hours = (Date.now() - stamped) / (1000 * 60 * 60);
      return hours <= 24;
    } catch { return true; }
  })();

  const submitFinalDecision = async () => {
    if (decisionChoice === "denied" && decisionReason.trim().length < 3) {
      toast.error("A reason (≥3 characters) is required for a Not Approved decision.");
      return;
    }
    setDecisionBusy(true);
    try {
      const { data } = await apiClient.post(`/screenings/${s.id}/final-decision`, {
        decision: decisionChoice,
        reason: decisionReason.trim() || null,
      });
      toast.success(`Decision recorded: ${FINAL_DECISION_LABEL[data.final_decision]}. Application archived.`);
      setDecisionOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not record decision.");
    } finally { setDecisionBusy(false); }
  };

  const heroStyle = HERO_STYLE[state] || HERO_STYLE.submitted;
  const isDark = isDecided;
  const headlineCls = isDark ? "text-white" : "text-[#0A192F]";
  const subCls = isDark ? "text-white/85" : "text-[#4A5568]";
  const reasonCls = isDark
    ? "bg-white/12 backdrop-blur-sm border-l-2 border-white text-white"
    : "bg-white/70 backdrop-blur-sm border-l-2 border-[#0A192F] text-[#0A192F]";
  const meta = isDark ? "text-white/70" : "text-[#4A5568]";

  // For Final Decisions the eyebrow reads "PM Final Decision"; for NTN
  // Recommendation states it reads "NTN Recommendation" so the two layers
  // never collapse into each other visually.
  const eyebrow = isFinal ? "PM Final Decision" : "NTN Recommendation";
  const headline = HEADLINE[state];
  const subline = SUBLINE_NEUTRAL[state] || (
    state === "ready"
      ? (isStaff ? "Set the recommendation when you've reviewed the file." : "NTN is finalising the recommendation. You'll make the final decision.")
      : ""
  );

  // PM can record/update the final decision once the file has reached a
  // workable state. We allow it any time after the 6-stage workflow reaches
  // "complete" (reports done) — the NTN Recommendation is optional but
  // surfaced as guidance.
  const pmCanDecide = isPM && s.overall_status === "complete" && !s.final_decision;
  // Adverse action is offered ONLY when the PM has issued a Not Approved
  // (denied) final decision. NTN recommendation states no longer trigger it.
  const showAdverseAction = isPM && state === "final_denied";

  return (
    <div className={`relative overflow-hidden rounded-lg border ${heroStyle} p-6 sm:p-8`} data-testid="decision-hero">
      <div className={`absolute inset-0 dotted-bg pointer-events-none ${isDark ? "opacity-15" : "opacity-30"}`} />
      <div className="relative flex flex-col sm:flex-row sm:items-start justify-between gap-5">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold mb-3 flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${isDark ? "bg-white/90" : "bg-slate-900"}`} />
            <span className={isDark ? "text-white/90" : "text-slate-500"} data-testid="decision-hero-eyebrow">{eyebrow}</span>
          </div>
          <h2 className={`ntn-h1 leading-none ${headlineCls}`} data-testid="decision-hero-headline">{headline}</h2>
          <p className={`mt-4 max-w-2xl leading-relaxed ntn-body ${subCls}`}
             style={isDark ? { color: "rgba(255,255,255,0.92)" } : undefined}>{subline}</p>

          {isFinal && s.final_decision_reason && (
            <div className={`mt-5 max-w-2xl p-4 text-sm ${reasonCls}`} data-testid="final-decision-reason">
              {s.final_decision_reason}
            </div>
          )}
          {isFinal && s.final_decision_by_name && (
            <div className={`text-[10px] font-mono mt-4 tracking-[0.12em] uppercase ${meta}`} data-testid="final-decision-meta">
              Closed by {s.final_decision_by_name} · {fmtDate(s.final_decision_at)} · Archived
            </div>
          )}

          {/* NTN Recommendation context still renders below a Final Decision so
              the PM can see what intelligence informed the closing outcome. */}
          {isRecommendation && s.recommendation_reason && (
            <div className={`mt-5 max-w-2xl p-4 text-sm ${reasonCls}`} data-testid="decision-reason">
              {s.recommendation_reason}
            </div>
          )}
          {isRecommendation && s.recommendation_by_name && (
            <div className={`text-[10px] font-mono mt-4 tracking-[0.12em] uppercase ${meta}`}>
              Set by {s.recommendation_by_name} · {fmtDate(s.recommendation_at)}
            </div>
          )}
          {isFinal && s.recommendation && (
            <div className={`mt-5 max-w-2xl p-3 rounded-md text-xs ${isDark ? "bg-white/10 text-white/85" : "bg-slate-50 text-slate-600"}`} data-testid="prior-recommendation-context">
              <span className="font-mono tracking-[0.14em] uppercase font-bold opacity-80">Prior NTN Recommendation · </span>
              <span className="font-semibold">{HEADLINE[s.recommendation] || s.recommendation}</span>
              {s.recommendation_by_name && <span className="opacity-70"> · by {s.recommendation_by_name}</span>}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 self-start sm:self-auto shrink-0">
          {/* Undo Final Decision — PM within 24h, staff any time. */}
          {isFinal && canUndoNow && (
            <Dialog open={undoOpen} onOpenChange={setUndoOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className={`rounded-md h-10 px-4 ${isDark
                    ? "bg-white/10 text-white border-white/30 hover:bg-white/20"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                  data-testid="open-undo-final-decision"
                >
                  Undo decision
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Undo Final Decision</DialogTitle>
                  <DialogDescription>
                    This reverses the prior Final Decision and re-opens the application for further action.
                    {isPM && (
                      <span className="block mt-1 text-slate-500 text-xs">
                        Property Managers can undo within 24 hours of the original decision. After that, contact NTN Support.
                      </span>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="undo-reason" className="text-sm">Reason (required)</Label>
                    <Textarea
                      id="undo-reason"
                      value={undoReason}
                      onChange={(e) => setUndoReason(e.target.value)}
                      placeholder="e.g., Wrong applicant selected; correcting."
                      className="mt-1"
                      data-testid="undo-final-decision-reason"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setUndoOpen(false)} data-testid="undo-final-decision-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={submitUndo}
                    disabled={undoBusy}
                    className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white"
                    data-testid="undo-final-decision-confirm"
                  >
                    {undoBusy ? "Reversing..." : "Confirm Undo"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* PM action: Set Final Decision (closes + archives the application). */}
          {pmCanDecide && (
            <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
              <DialogTrigger asChild>
                <Button className={`rounded-md h-11 px-5 ${
                  isDark ? "bg-white text-slate-900 hover:bg-white/90" : "bg-[#0A192F] hover:bg-[#0A192F]/90 text-white"
                }`} data-testid="open-final-decision">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Record Final Decision
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Record Final Decision</DialogTitle>
                  <DialogDescription>
                    Closing this application archives it from active queues and routes it to the Closed Applications Archive.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  {FINAL_OPTIONS.map((opt) => {
                    const active = decisionChoice === opt.value;
                    const tone = OPTION_TONE[opt.tone];
                    const Icon = opt.Icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setDecisionChoice(opt.value)}
                        className={`w-full text-left rounded-md border p-3 flex items-start gap-3 transition-all ${active
                          ? tone.active
                          : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                        data-testid={`final-decision-option-${opt.value}`}
                      >
                        <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${active ? tone.icon : "bg-slate-100 text-slate-500"}`}>
                          <Icon className="h-5 w-5" strokeWidth={2} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900">{opt.label}</div>
                          <div className="text-xs text-slate-600 mt-0.5">{opt.blurb}</div>
                        </div>
                      </button>
                    );
                  })}
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
                      Reason {decisionChoice === "denied" ? <span className="text-rose-600">· required</span> : <span className="text-slate-400">· optional</span>}
                    </Label>
                    <Textarea
                      rows={3}
                      value={decisionReason}
                      onChange={(e) => setDecisionReason(e.target.value)}
                      placeholder={decisionChoice === "denied"
                        ? "FCRA-style reason for declining. Sent on the Adverse Action Notice."
                        : "Optional context for your records (not sent to the applicant)."}
                      className="mt-1.5 rounded-sm"
                      data-testid="final-decision-reason-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={submitFinalDecision}
                    disabled={decisionBusy}
                    className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm"
                    data-testid="final-decision-submit"
                  >
                    {decisionBusy ? "Recording…" : "Record decision & archive"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Staff action: Set NTN Recommendation (intelligence layer). */}
          {isStaff && s.overall_status === "complete" && !s.final_decision && (
            <Dialog open={recOpen} onOpenChange={setRecOpen}>
              <DialogTrigger asChild>
                <Button className={`rounded-md h-11 px-5 ${
                  isDark ? "bg-white text-slate-900 hover:bg-white/90" : "bg-[#0A192F] hover:bg-[#0A192F]/90 text-white"
                }`} data-testid="open-recommendation">
                  <Gavel className="h-4 w-4 mr-2" /> {s.recommendation ? "Update recommendation" : "Set recommendation"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Set recommendation</DialogTitle>
                  <DialogDescription>The property manager will be emailed your decision.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Decision</Label>
                    <Select value={rec} onValueChange={setRec}>
                      <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="rec-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approve">Recommended</SelectItem>
                        <SelectItem value="conditional">Recommended with Conditions</SelectItem>
                        <SelectItem value="deny">Not Recommended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Reason (sent to PM)</Label>
                    <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Why this recommendation? Include conditions if applicable."
                      className="mt-1.5 rounded-sm" data-testid="rec-reason" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submitRec} disabled={recBusy} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="rec-submit">
                    {recBusy ? "Saving…" : "Send recommendation"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Adverse action — ONLY for final_denied (PM has actually declined). */}
          {showAdverseAction && <AdverseActionDialog screening={s} onIssued={reload} />}
        </div>
      </div>
    </div>
  );
}
